import React, { useContext, MouseEvent, useEffect, useRef, useState } from 'react';

// MUI
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';

// Custom Components
import UserContext from '@/context/UserContext/UserContext';

// Types
import { ITableButtonProps, AllowType } from '@/@types/ui/Table';
import { DCRecord } from '@/@types/lib/dataController';

const TableButton: React.FC<ITableButtonProps> = (props) => {
  const userContext = useContext(UserContext);
  const containerRef = useRef<HTMLSpanElement>(null);
  const [toolbarWidth, setToolbarWidth] = useState<number>(Infinity);

  const {
    allow = 'always',
    selection,
    onClick,
    needPermission,
    isEnabledRecord,
    tooltip,
    startIcon,
    variant = 'contained',
    children,
    ...otherProps
  } = props;

  useEffect(() => {
    const toolbar = containerRef.current?.closest('.MuiToolbar-root');

    if (!toolbar) return undefined;

    const updateWidth = () => {
      setToolbarWidth(toolbar.getBoundingClientRect().width);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(toolbar);

    return () => observer.disconnect();
  }, []);

  const isEnabled = (allowType: AllowType, sel: { [key: string]: DCRecord }): boolean => {
    switch (allowType) {
      case 'always':
        return true;
      case 'one':
        return Object.keys(sel).length === 1;
      case 'many':
        return Object.keys(sel).length > 0;
      default:
        return false;
    }
  };

  const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
    onClick(evt, selection);
  };

  const disabled = !isEnabled(allow, selection) || (isEnabledRecord ? !isEnabledRecord(selection) : false);

  if (needPermission && userContext) {
    const { hasAnyPermission } = userContext;
    if (!hasAnyPermission(needPermission)) return null;
  }

  const isSmall = toolbarWidth < 800;

  // icon-only Fab falls back to the label; a full button only shows an extra hint
  const fabTitle = tooltip || children;

  const button = isSmall ? (
    <Fab
      color="primary"
      size="small"
      disabled={disabled}
      onClick={handleClick}
      aria-label={typeof fabTitle === 'string' ? fabTitle : undefined}
      sx={{
        height: 28,
        minHeight: 28,
        width: 28,
        minWidth: 28,
        mx: '4px',
        padding: 0,

        ...(variant === 'outlined'
          ? {
              backgroundColor: 'transparent',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.main',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: 'action.disabled',
                borderColor: 'action.disabled',
                boxShadow: 'none',
              },
            }
          : {}),

        '& .MuiSvgIcon-root': {
          fontSize: 18,
        },
      }}
    >
      {startIcon ?? children}
    </Fab>
  ) : (
    <Button
      color="primary"
      size="small"
      variant={variant}
      {...otherProps}
      startIcon={startIcon}
      disabled={disabled}
      onClick={handleClick}
      sx={{
        ml: 1,
        mr: 0,
      }}
    >
      {children}
    </Button>
  );

  // span so the tooltip also shows on a disabled button
  return (
    <span ref={containerRef}>
      <Tooltip title={isSmall ? fabTitle : tooltip ?? ''} arrow placement="bottom">
        <span>{button}</span>
      </Tooltip>
    </span>
  );
};

export default TableButton;
